import { AnimatePresence, motion } from "framer-motion";
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { MdOutlineInfo } from "react-icons/md";
import InstructionsDraggablePanel from "../instructions-draggable-panel";
import { ScratchEnvironmentContext } from "components/utils/Contexts";
import PDFDraggablePanel from "./PDFDraggablePanel";

interface InstructionsButtonProps {
  ref?: any;
}

const InstructionsButton: React.FC<InstructionsButtonProps> = forwardRef(
  ({}, ref) => {
    // Constants
    const INITIAL_X = window.innerWidth * 0.55;
    const INITIAL_Y = window.innerHeight * 0.15;

    // States definition
    const [showPDFPanel, setShowPDFPanel] = useState(false);
    const [triggerShowPDFPanel, setTriggerShowPDFPanel] = useState(false);
    const [triggerHidePDFPanel, setTriggerHidePDFPanel] = useState(false);
    const [anchorPos, setAnchorPos] = useState({ x: 0, y: 0 });
    const [buttonWidth, setButtonWidth] = useState(0);
    const [buttonHeight, setButtonHeight] = useState(0);

    // Auxiliar state for getting the PDF panel position
    const [panelPosition, setPanelPosition] = useState({ x: 0, y: 0 });

    // Button ref definition
    const instructionsButtonRef = useRef<HTMLDivElement>(null);
    // And placeholder ref definition
    const placeholderButtonRef = useRef<HTMLDivElement>(null);

    // Load step-by-step and/or PDF instructions to decide button behaviour
    const { stepByStepInstructions, pdf } = useContext(
      ScratchEnvironmentContext
    );

    /*
     * To control internal functions from outside
     */
    useImperativeHandle(ref, () => ({
      handleClosePDFPanel: (anchorPos?: { x: number; y: number }) => {
        // Set the anchor pos (if any), and then execute it
        handleClosePDFPanel(anchorPos)();
      },
      setTriggerShowPDFPanel(bool: boolean) {
        setTriggerShowPDFPanel(bool);
      },
    }));

    /**
     * Function used when clicking the closing button
     */
    const handleClosePDFPanel = (anchorPos?: { x: number; y: number }) => {
      return () => {
        /**
         * Function used to update the states that contain the position
         * of the invisible instructions button (used as an anchor to
         * animate the draggable panel)
         */
        if (anchorPos) {
          setAnchorPos(anchorPos);
        } else if (placeholderButtonRef.current) {
          // Get the values of the position of the instructions button
          const { x, y } = placeholderButtonRef.current.getBoundingClientRect();

          // Update the states
          setAnchorPos({ x, y });
        }

        // Triggers the close panel function
        setTriggerHidePDFPanel(true);

        // And reset the panelPosition number
        setPanelPosition({ x: INITIAL_X, y: INITIAL_Y });
      };
    };

    /**
     * Function used to retrieve the original position of the instructions button
     * before start opening the PDF panel
     */
    useEffect(() => {
      if (triggerShowPDFPanel) {
        // Get the current value before dissapearing
        if (instructionsButtonRef.current) {
          // Get the values of the position of the instructions button
          const { x, y, width, height } =
            instructionsButtonRef.current.getBoundingClientRect();

          // Update the states
          setAnchorPos({ x, y });
          setButtonWidth(width);
          setButtonHeight(height);
          setPanelPosition({ x: INITIAL_X, y: INITIAL_Y });
        }

        // Trigger the showPDF as proper
        setShowPDFPanel(true);

        // And turn off the trigger
        setTriggerShowPDFPanel(false);
      }
    }, [triggerShowPDFPanel]);

    /**
     * Function used to retrieve the original position of the instructions button
     * before start opening the PDF panel
     */
    useEffect(() => {
      if (triggerHidePDFPanel) {
        // Trigger the close PDF as proper
        setShowPDFPanel(false);

        // And turn off the trigger
        setTriggerHidePDFPanel(false);
      }
    }, [triggerHidePDFPanel]);

    return (
      <>
        <AnimatePresence mode={showPDFPanel ? "sync" : "wait"}>
          {!showPDFPanel && (
            <motion.div
              id="scratch-instructions-button"
              key="scratch-instructions-button"
              ref={instructionsButtonRef}
              whileHover={{ scale: 1.06 }}
              className="h-[29px] px-3 py-1.5 rounded-full bg-neutral-50 ease-in-out duration-150 justify-center items-center gap-2 inline-flex cursor-pointer"
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={() => setTriggerShowPDFPanel(true)}
            >
              <div className="text-secondary-600 text-sm font-semibold font-poppins leading-[17px] z-10">
                Instructions
              </div>
              <div className="w-4 h-4 relative">
                <MdOutlineInfo className="w-full h-full fill-secondary-600" />
              </div>
            </motion.div>
          )}
          {showPDFPanel && (
            <>
              <motion.div
                key="floating-button"
                id="floating-button"
                className="fixed inset-0 w-fit h-[29px] px-3 py-1.5 rounded-full bg-neutral-50 justify-center items-center gap-2 inline-flex cursor-pointer z-10 select-none"
                initial={{ opacity: 1, ...anchorPos }}
                animate={{ opacity: 0, x: panelPosition.x, y: panelPosition.y }}
                exit={{
                  opacity: 1,
                  ...anchorPos,
                }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                <div className="text-secondary-600 text-sm font-semibold font-poppins leading-[17px] z-10">
                  Instructions
                </div>
                <div className="w-4 h-4 relative">
                  <MdOutlineInfo className="w-full h-full fill-secondary-600" />
                </div>
              </motion.div>
              {stepByStepInstructions?.length ? (
                <InstructionsDraggablePanel
                  handleClosePanel={handleClosePDFPanel()}
                  initial={{
                    ...anchorPos,
                    opacity: 0,
                    width: buttonWidth,
                    height: buttonHeight,
                  }}
                  animate={{
                    opacity: 1,
                    x: INITIAL_X,
                    y: INITIAL_Y,
                  }}
                  exit={{
                    ...anchorPos,
                    opacity: 0,
                    width: buttonWidth,
                    height: buttonHeight,
                  }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  panelPosition={panelPosition}
                  setPanelPosition={setPanelPosition}
                />
              ) : (
                <PDFDraggablePanel
                  pdf={pdf}
                  handleClosePanel={handleClosePDFPanel()}
                  initial={{
                    ...anchorPos,
                    opacity: 0,
                    width: buttonWidth,
                    height: buttonHeight,
                  }}
                  animate={{
                    opacity: 1,
                    x: INITIAL_X,
                    y: INITIAL_Y,
                  }}
                  exit={{
                    ...anchorPos,
                    opacity: 0,
                    width: buttonWidth,
                    height: buttonHeight,
                  }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  panelPosition={panelPosition}
                  setPanelPosition={setPanelPosition}
                />
              )}
            </>
          )}
        </AnimatePresence>
        <div
          ref={placeholderButtonRef}
          id="scratch-instructions-button-placeholder"
          key="scratch-instructions-button-placeholder"
          className="h-[29px] px-3 py-1.5 rounded-full bg-neutral-50 invisible"
        />
      </>
    );
  }
);

export default InstructionsButton;
